import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatDate, formatTime, STATUS_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function toLocalISO(d) {
  // returns yyyy-MM-ddTHH:mm in local time
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d - tzOffset).toISOString().slice(0, 16);
}

export default function LessonDialog({ open, onOpenChange, students, prefill, onSaved }) {
  const { user } = useAuth();
  const currency = user?.currency || "TRY";
  const [form, setForm] = useState({
    student_id: "", teacher_id: "", start_time: "", end_time: "",
    subject: "", notes: "", rate_override: "",
  });
  const [teachers, setTeachers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (open) {
      api.get("/teachers").then((r) => setTeachers(r.data)).catch(() => setTeachers([]));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      const end = new Date(now);
      end.setHours(now.getHours() + 1);
      setForm((f) => ({
        student_id: students[0]?.id || "",
        teacher_id: f.teacher_id || teachers[0]?.id || "",
        start_time: prefill?.start_time || toLocalISO(now),
        end_time: prefill?.end_time || toLocalISO(end),
        subject: "",
        notes: "",
        rate_override: "",
      }));
      setConflict(null);
    }
  }, [open, prefill, students, teachers]);

  async function submit(force = false) {
    if (!form.student_id) return toast.error("Öğrenci seçin.");
    setSaving(true);
    try {
      // Send the datetime-local input string AS-IS (naive local). Backend stores naive,
      // matching how recurring lessons (from schedule_slots) are stored. Converting via
      // new Date(...).toISOString() would shift to UTC and cause false conflict warnings
      // and lessons appearing at the wrong hour for non-UTC users (e.g. Turkey UTC+3).
      const startStr = form.start_time?.length === 16 ? `${form.start_time}:00` : form.start_time;
      const endStr = form.end_time?.length === 16 ? `${form.end_time}:00` : form.end_time;
      await api.post("/lessons", {
        student_id: form.student_id,
        teacher_id: form.teacher_id || null,
        start_time: startStr,
        end_time: endStr,
        subject: form.subject,
        notes: form.notes,
        rate_override: form.rate_override === "" ? null : Number(form.rate_override),
      }, { params: force ? { force: true } : {} });
      toast.success("Ders eklendi.");
      setConflict(null);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.conflict) {
        setConflict(detail.conflict);
      } else {
        toast.error(typeof detail === "string" ? detail : "Eklenemedi.");
      }
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setConflict(null); onOpenChange(v); }}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Yeni ders</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Öğrenci</Label>
            <Select value={form.student_id} onValueChange={(v) => { setForm({ ...form, student_id: v }); setConflict(null); }}>
              <SelectTrigger data-testid="lesson-form-student"><SelectValue placeholder="Öğrenci seçin" /></SelectTrigger>
              <SelectContent>
                {students.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Önce öğrenci ekleyin</div>
                ) : students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {teachers.length > 0 && (
            <div className="space-y-1.5">
              <Label>Öğretmen</Label>
              <Select value={form.teacher_id} onValueChange={(v) => { setForm({ ...form, teacher_id: v }); setConflict(null); }}>
                <SelectTrigger data-testid="lesson-form-teacher"><SelectValue placeholder="Öğretmen seçin" /></SelectTrigger>
                <SelectContent>
                  {teachers.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                        {t.name}{t.subject ? ` · ${t.subject}` : ""}{Number(t.hourly_rate) > 0 ? ` · ${formatCurrency(t.hourly_rate, currency)}/saat` : ""}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(() => {
                const t = teachers.find((x) => x.id === form.teacher_id);
                const student = students.find((x) => x.id === form.student_id);
                const teacherRate = Number(t?.hourly_rate || 0);
                const studentRate = Number(student?.hourly_rate || 0);
                const stRow = (student?.teacher_rates || []).find((r) => r.teacher_id === form.teacher_id);
                const stRate = Number(stRow?.hourly_rate || 0);
                if (form.rate_override !== "" && form.rate_override !== null && Number(form.rate_override) >= 0) {
                  return <p className="text-xs text-muted-foreground">Sabit ücret: <strong>{formatCurrency(Number(form.rate_override), currency)}</strong> (saat farketmez).</p>;
                }
                let effective = 0;
                let source = "";
                if (stRate > 0) { effective = stRate; source = `${student?.name || "öğrenci"} → ${t?.name || "öğretmen"} özel`; }
                else if (teacherRate > 0) { effective = teacherRate; source = `${t?.name || "öğretmen"} varsayılan`; }
                else if (studentRate > 0) { effective = studentRate; source = `${student?.name || "öğrenci"} varsayılan`; }
                if (effective > 0) {
                  return (
                    <p className="text-xs text-muted-foreground">
                      Saatlik ücret: <strong>{formatCurrency(effective, currency)}</strong> · kaynak: {source}
                    </p>
                  );
                }
                return <p className="text-xs text-[#D97757]">Saatlik ücret tanımsız (öğrenci-öğretmen, öğretmen ve öğrenci ücreti boş). Ders 0 ₺ kaydedilecek — aşağıdaki "Ücret" alanından elle girebilirsiniz.</p>;
              })()}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Başlangıç</Label>
              <Input type="datetime-local" value={form.start_time} onChange={(e) => { setForm({ ...form, start_time: e.target.value }); setConflict(null); }} data-testid="lesson-form-start" />
            </div>
            <div className="space-y-1.5">
              <Label>Bitiş</Label>
              <Input type="datetime-local" value={form.end_time} onChange={(e) => { setForm({ ...form, end_time: e.target.value }); setConflict(null); }} data-testid="lesson-form-end" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Konu</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Ücret (opsiyonel, boşsa saatlik × süre)</Label>
            <Input type="number" step="0.01" value={form.rate_override} onChange={(e) => setForm({ ...form, rate_override: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Not</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          {conflict && (
            <div className="border border-[#FFCC99] bg-[#FFF3E0] rounded-md p-3 space-y-2" data-testid="conflict-warning">
              <div className="flex items-start gap-2">
                <WarningCircle size={18} weight="duotone" className="text-[#E65100] mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold text-[#E65100]">
                    {(conflict.kinds || []).includes("teacher") && (conflict.kinds || []).includes("student")
                      ? "Öğretmen + Öğrenci çakışması"
                      : (conflict.kinds || []).includes("teacher")
                        ? "Öğretmen çakışması"
                        : (conflict.kinds || []).includes("student")
                          ? "Öğrenci çakışması"
                          : "Saat çakışması"}
                  </div>
                  <div className="text-[#8B4500] mt-1">
                    {conflict.teacher_name && <><span className="font-medium">{conflict.teacher_name}</span>{" + "}</>}
                    <span className="font-medium">{conflict.student_name}</span> için zaten bir ders var:
                    <br />
                    {formatDate(conflict.start_time)} · {formatTime(conflict.start_time)}–{formatTime(conflict.end_time)} ({STATUS_LABELS[conflict.status]})
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Yine de eklemek isterseniz aynı saatte birden çok ders olacak.
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setConflict(null)}>Saati değiştir</Button>
                <Button size="sm" className="bg-[#D97757] hover:bg-[#BF6446] text-white" onClick={() => submit(true)} data-testid="conflict-force-add">
                  Yine de ekle
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
          <Button onClick={() => submit(false)} disabled={saving || !!conflict} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="lesson-form-save">
            {saving ? "Kaydediliyor…" : "Ekle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
